import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createChannelConfig,
  getApiErrorMessage,
  getChannelConfig,
  listChannelTypeOptions,
  listDepartments,
  updateChannelConfig,
} from '../../api/client';
import type { ChannelConfig, ChannelTypeOption, DepartmentSummary } from '../../api/types';
import { CopyTextFallbackModal } from '../../components/CopyTextFallbackModal';
import { WidgetSecretCredentials } from '../../components/WidgetSecretCredentials';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { copyTextToClipboardSync } from '../../utils/clipboard';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { maskCredentials } from '../../utils/maskCredentials';
import {
  generateWidgetSecret,
  isWidgetEmbedChannelType,
  parseWidgetSecretFromCredentials,
  suggestedWidgetIdentifier,
  widgetCredentialsPayload,
} from '../../utils/widgetChannel';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function ChannelFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();

  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [channelTypes, setChannelTypes] = useState<ChannelTypeOption[]>([]);
  const [departmentId, setDepartmentId] = useState('');
  const [channelTypeId, setChannelTypeId] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [label, setLabel] = useState('');
  const [credentialsJson, setCredentialsJson] = useState('');
  const [storedCredentials, setStoredCredentials] = useState<Record<string, unknown> | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copyFallback, setCopyFallback] = useState<{ title: string; text: string } | null>(null);

  const selectedType = channelTypes.find((t) => t.id === channelTypeId);
  const isWidgetChannel = isWidgetEmbedChannelType(selectedType?.code);

  const storedWidgetSecret = useMemo(
    () => parseWidgetSecretFromCredentials(storedCredentials),
    [storedCredentials],
  );

  const draftWidgetSecret = useMemo(() => {
    if (!credentialsJson.trim()) return null;
    try {
      return parseWidgetSecretFromCredentials(JSON.parse(credentialsJson) as Record<string, unknown>);
    } catch {
      return null;
    }
  }, [credentialsJson]);

  const maskedCredentialsJson = useMemo(() => {
    if (!storedCredentials || isWidgetChannel) return '';
    return JSON.stringify(maskCredentials(storedCredentials), null, 2);
  }, [storedCredentials, isWidgetChannel]);

  const canEdit = currentUser && (isNew ? canWritePlatformConfig(currentUser) : canEditPlatformConfig(currentUser));

  useEffect(() => {
    Promise.all([listDepartments(), listChannelTypeOptions()])
      .then(([depts, types]) => {
        setDepartments(depts);
        setChannelTypes(types);
        if (isNew) {
          if (depts[0]) setDepartmentId(depts[0].id);
          if (types[0]) setChannelTypeId(types[0].id);
        }
      })
      .catch(() => undefined);
  }, [isNew]);

  useEffect(() => {
    if (isNew || !id) return;
    getChannelConfig(id)
      .then((c: ChannelConfig) => {
        setDepartmentId(c.departmentId);
        setChannelTypeId(c.channelTypeId);
        setIdentifier(c.identifier);
        setLabel(c.label);
        setStoredCredentials(c.credentials ?? null);
        setIsActive(c.isActive);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    if (!selectedType?.code) return;
    const suggested = suggestedWidgetIdentifier(selectedType.code);
    if (!suggested) return;
    setIdentifier((prev) => {
      const trimmed = prev.trim();
      if (!trimmed || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return suggested;
      }
      return prev;
    });
  }, [selectedType?.code]);

  function parseCredentials(): Record<string, unknown> | undefined {
    const trimmed = credentialsJson.trim();
    if (!trimmed) return undefined;
    return JSON.parse(trimmed) as Record<string, unknown>;
  }

  function applyWidgetSecret(secret: string) {
    setCredentialsJson(JSON.stringify(widgetCredentialsPayload(secret), null, 2));
  }

  function resolveCredentialsForSave(): Record<string, unknown> | undefined {
    if (isWidgetChannel) {
      const secret = draftWidgetSecret ?? (isNew ? null : storedWidgetSecret);
      if (!secret) return undefined;
      return widgetCredentialsPayload(secret);
    }
    return credentialsJson.trim() ? parseCredentials() : undefined;
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      let credentials: Record<string, unknown> | undefined;
      try {
        credentials = resolveCredentialsForSave();
      } catch {
        setError('Credentials must be valid JSON');
        return;
      }

      if (isWidgetChannel && isNew && !credentials?.widgetSecret) {
        setError('Generate a widget secret before saving.');
        return;
      }

      if (isNew) {
        const created = await createChannelConfig({
          departmentId,
          channelTypeId,
          identifier: identifier.trim(),
          label: label.trim(),
          credentials,
          isActive,
        });
        navigate(PLATFORM_CONFIG.channel(created.id));
      } else if (id) {
        await updateChannelConfig(id, {
          departmentId,
          channelTypeId,
          identifier: identifier.trim(),
          label: label.trim(),
          ...(credentials ? { credentials } : {}),
          isActive,
        });
        const updated = await getChannelConfig(id);
        setStoredCredentials(updated.credentials ?? null);
        setCredentialsJson('');
        showToast({ title: 'Saved', label: 'Channel config updated' });
        navigate(PLATFORM_CONFIG.channels);
      }
    } catch (err) {
      setError(
        err instanceof SyntaxError
          ? 'Credentials must be valid JSON'
          : getApiErrorMessage(err),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleClearCredentials() {
    if (!id || isNew) return;
    if (
      !window.confirm(
        'Clear stored credentials? Widget embeds will fail until a new widget secret is generated and saved.',
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateChannelConfig(id, { credentials: null });
      setCredentialsJson('');
      setStoredCredentials(null);
      showToast({ title: 'Cleared', label: 'Credentials removed' });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function copyWithFallback(title: string, text: string) {
    if (copyTextToClipboardSync(text)) {
      showToast({ title: 'Copied', label: title });
      return;
    }
    setCopyFallback({ title: 'Copy credentials', text });
    showToast({
      title: 'Select and copy',
      label: 'Automatic copy blocked — use the dialog that opened',
    });
  }

  function copyActiveWidgetSecret(asJson: boolean) {
    const secret = draftWidgetSecret ?? storedWidgetSecret;
    if (!secret) return;
    const text = asJson
      ? JSON.stringify(widgetCredentialsPayload(secret), null, 2)
      : secret;
    copyWithFallback(
      asJson ? 'Credentials JSON copied to clipboard' : 'widgetSecret copied to clipboard',
      text,
    );
  }

  function handleCopyCredentials() {
    if (!storedCredentials) return;
    copyWithFallback(
      'Full credentials JSON copied to clipboard',
      JSON.stringify(storedCredentials, null, 2),
    );
  }

  function handleGenerateWidgetSecret() {
    applyWidgetSecret(generateWidgetSecret());
    showToast({
      title: 'Secret generated',
      label: isNew ? 'Copy and share with the host app, then save' : 'Save changes to apply',
    });
  }

  function handleRegenerateWidgetSecret() {
    const message = storedWidgetSecret
      ? 'Regenerating will invalidate the current widget secret. Garages/FLOWW must update their signing key before embeds work again. Continue?'
      : 'Generate a new widget secret?';
    if (!window.confirm(message)) return;
    handleGenerateWidgetSecret();
  }

  if (!canEdit) {
    return (
      <div className="alert alert-error">
        Only users with Channel Management write/edit permission can manage channels.{' '}
        <Link to={PLATFORM_CONFIG.channels}>Back to list</Link>
      </div>
    );
  }

  if (loading) return <div className="loading">Loading…</div>;

  const identifierPlaceholder = isWidgetChannel
    ? suggestedWidgetIdentifier(selectedType!.code) ?? 'GARAGES'
    : selectedType?.code === 'EMAIL'
      ? 'support@21kschool.com'
      : 'endpoint, phone number, or logical key';

  return (
    <>
      <CopyTextFallbackModal
        open={Boolean(copyFallback)}
        title={copyFallback?.title ?? 'Copy'}
        text={copyFallback?.text ?? ''}
        onClose={() => setCopyFallback(null)}
      />
      <div className="page-header">
        <div>
          <Link to={PLATFORM_CONFIG.channels} style={{ fontSize: '0.85rem' }}>
            ← Channel config
          </Link>
          <h1 style={{ marginTop: '0.35rem' }}>
            {isNew ? 'New channel config' : 'Edit channel config'}
          </h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="card card-body" style={{ maxWidth: 640 }} onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="ch-dept">Department</label>
          <select
            id="ch-dept"
            className="form-control"
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            required
          >
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="ch-channel">Channel type</label>
          <select
            id="ch-channel"
            className="form-control"
            value={channelTypeId}
            onChange={(e) => setChannelTypeId(e.target.value)}
            required
          >
            {channelTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.code})
              </option>
            ))}
          </select>
          <small style={{ color: 'var(--text-muted)' }}>
            <Link to={PLATFORM_CONFIG.channelTypes}>Manage channel types</Link>
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="ch-label">Label</label>
          <input
            id="ch-label"
            className="form-control"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            placeholder="e.g. Main CX Email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="ch-id">Identifier</label>
          <input
            id="ch-id"
            className="form-control"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
            placeholder={identifierPlaceholder}
          />
          <small style={{ color: 'var(--text-muted)' }}>
            {isWidgetChannel
              ? 'Widget source code — must match JWT source and SupportWidget.init({ source }) (e.g. GARAGES, FLOWW). Not a URL.'
              : 'Inbound routing key: email address, phone number, or logical endpoint per channel.'}
          </small>
        </div>

        <div className="form-group">
          <span className="form-label-block">
            {isWidgetChannel ? 'Widget credentials' : 'Credentials (JSON)'}
          </span>

          {isWidgetChannel ? (
            <WidgetSecretCredentials
              storedSecret={storedWidgetSecret}
              draftSecret={draftWidgetSecret}
              saving={saving}
              onGenerate={handleGenerateWidgetSecret}
              onRegenerate={handleRegenerateWidgetSecret}
              onCopySecret={() => void copyActiveWidgetSecret(false)}
              onCopyJson={() => void copyActiveWidgetSecret(true)}
            />
          ) : (
            <>
              {storedCredentials && !credentialsJson.trim() && (
                <div className="channel-creds-preview">
                  <div className="channel-creds-preview-head">
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Stored credentials (values masked)
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={saving}
                      onClick={() => void handleCopyCredentials()}
                    >
                      Copy full JSON
                    </button>
                  </div>
                  <pre className="channel-creds-masked" aria-label="Masked credentials">
                    {maskedCredentialsJson}
                  </pre>
                </div>
              )}
              <label htmlFor="ch-creds" className="text-muted channel-creds-replace-label">
                {storedCredentials ? 'Replace credentials (optional)' : 'Enter credentials JSON'}
              </label>
              <textarea
                id="ch-creds"
                className="form-control"
                rows={4}
                value={credentialsJson}
                onChange={(e) => setCredentialsJson(e.target.value)}
                placeholder={
                  storedCredentials
                    ? '{"apiToken":"..."} — leave blank to keep current credentials'
                    : '{"apiToken":"..."}'
                }
              />
            </>
          )}

          {(storedCredentials || storedWidgetSecret) && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ marginTop: '0.75rem' }}
              disabled={saving}
              onClick={() => void handleClearCredentials()}
            >
              Clear stored credentials
            </button>
          )}
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />{' '}
            Active
          </label>
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create config' : 'Save changes'}
        </button>
      </form>
    </>
  );
}
